import React from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaCheck, FaTimes, FaArrowRight, FaShieldAlt, FaClock, FaMobileAlt } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const Reviews: React.FC = () => {
  const bookmakers = [
    {
      id: 'winamax',
      name: 'Winamax',
      logo: '/images/offers/winamax.webp',
      url: 'https://www.winamax.fr/',
      rating: 4.8,
      bonus: '150€',
      minDeposit: '10€',
      payoutSpeed: '12h',
      license: 'ANJ',
      pros: [
        'Bonus de bienvenue très généreux',
        'Promotions quotidiennes attractives',
        'Poker intégré de qualité',
        'Retraits ultra-rapides',
      ],
      cons: [
        'Cotes légèrement inférieures sur certains marchés',
        'Interface nécessite un temps d\'adaptation',
      ],
      description: 'Winamax se distingue par son bonus de bienvenue exceptionnel et ses promotions régulières. La plateforme combine paris sportifs et poker, offrant une expérience unique aux joueurs français.',
    },
    {
      id: 'bwin',
      name: 'Bwin',
      logo: '/images/offers/bwin.webp',
      url: 'https://bwin.fr/',
      rating: 4.7,
      bonus: '100€',
      minDeposit: '10€',
      payoutSpeed: '24h',
      license: 'ANJ',
      pros: [
        'Cotes parmi les plus élevées du marché',
        'Cash-out disponible sur tous les sports',
        'Streaming en direct de nombreux événements',
        'Application mobile très performante',
      ],
      cons: [
        'Interface parfois chargée',
        'Limites de paris parfois restrictives',
      ],
      description: 'Bwin s\'impose comme l\'un des leaders du marché français grâce à ses cotes compétitives et son offre complète. Le site propose une expérience utilisateur solide avec des fonctionnalités avancées comme le cash-out et le streaming live.',
    },
    {
      id: 'unibet',
      name: 'Unibet',
      logo: '/images/offers/unibet.webp',
      url: 'https://www.unibet.fr/',
      rating: 4.6,
      bonus: '120€',
      minDeposit: '10€',
      payoutSpeed: '24h',
      license: 'ANJ',
      pros: [
        'Programme VIP très avantageux',
        'Bonus récurrents pour les joueurs actifs',
        'Couverture sportive impressionnante',
        'Support client réactif',
      ],
      cons: [
        'Bonus de bienvenue moins attractif que la concurrence',
        'Interface moins moderne que certains concurrents',
      ],
      description: 'Unibet brille par sa fidélité envers ses joueurs avec un programme VIP généreux et des bonus réguliers. La plateforme offre une couverture complète des sports et des marchés variés.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Avis des Meilleurs Bookmakers en France',
    description: 'Comparaison détaillée et avis experts sur les meilleurs sites de paris sportifs français.',
    itemListElement: bookmakers.map((bookmaker, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Review',
        itemReviewed: {
          '@type': 'Organization',
          name: bookmaker.name,
        },
        author: {
          '@type': 'Organization',
          name: 'Parismin',
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: bookmaker.rating,
          bestRating: 5,
        },
      },
    })),
  };

  return (
    <Layout>
      <SEO
        title="Avis Complets des Bookmakers Français 2025"
        description="Découvrez nos avis détaillés et comparatifs sur les meilleurs bookmakers français. Analyses approfondies, bonus, cotes et expérience utilisateur."
        url="/reviews"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">Avis Détaillés</span> des Bookmakers
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Notre équipe teste chaque plateforme pour vous offrir des analyses complètes et objectives. 
            Découvrez les forces et faiblesses de chaque bookmaker.
          </p>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {bookmakers.map((bookmaker) => (
              <article
                key={bookmaker.id}
                id={bookmaker.id}
                className="glass rounded-3xl p-8 md:p-12 card-hover"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Left Column - Main Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="flex items-center space-x-4 mb-2 flex-wrap">
                          <img 
                            src={bookmaker.logo} 
                            alt={bookmaker.name}
                            className="h-16 w-auto object-contain"
                            width="166"
                            height="64"
                            loading="lazy"
                            decoding="async"
                          />
                          <h2 className="text-3xl font-bold heading-font">{bookmaker.name}</h2>
                        </div>
                        <div className="flex items-center space-x-2 mb-4">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <FaStar
                                key={i}
                                className={`${
                                  i < Math.floor(bookmaker.rating)
                                    ? 'text-yellow-400'
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-2xl font-bold ml-2">{bookmaker.rating}/5</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-400 mb-1">Bonus</div>
                        <div className="text-2xl font-bold text-gradient">{bookmaker.bonus}</div>
                      </div>
                    </div>

                    <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                      {bookmaker.description}
                    </p>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="glass rounded-xl p-4 text-center">
                        <div className="text-sm text-gray-400 mb-1">Dépôt min.</div>
                        <div className="font-bold">{bookmaker.minDeposit}</div>
                      </div>
                      <div className="glass rounded-xl p-4 text-center">
                        <div className="text-sm text-gray-400 mb-1">Retrait</div>
                        <div className="font-bold flex items-center justify-center space-x-1">
                          <FaClock className="text-sm" />
                          <span>{bookmaker.payoutSpeed}</span>
                        </div>
                      </div>
                      <div className="glass rounded-xl p-4 text-center">
                        <div className="text-sm text-gray-400 mb-1">Licence</div>
                        <div className="font-bold flex items-center justify-center space-x-1">
                          <FaShieldAlt className="text-sm" />
                          <span>{bookmaker.license}</span>
                        </div>
                      </div>
                    </div>

                    {/* Pros and Cons */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-bold text-lg mb-3 flex items-center space-x-2">
                          <FaCheck className="text-green-400" />
                          <span>Points forts</span>
                        </h3>
                        <ul className="space-y-2">
                          {bookmaker.pros.map((pro, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-300">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-3 flex items-center space-x-2">
                          <FaTimes className="text-red-400" />
                          <span>Points faibles</span>
                        </h3>
                        <ul className="space-y-2">
                          {bookmaker.cons.map((con, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-gray-300">
                              <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="mt-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                  <a
                    href={bookmaker.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-xl font-semibold text-white hover:shadow-lg hover:shadow-purple-500/50 transition-all"
                  >
                    <span>Visiter le site</span>
                    <FaArrowRight />
                  </a>
                  <Link
                    to={`/comparisons#${bookmaker.id}`}
                    className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 glass rounded-xl font-semibold text-gray-300 hover:text-white hover:bg-white/10 transition-all"
                  >
                    <span>Comparer avec les autres</span>
                    <FaArrowRight />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Reviews;

